import React, { memo, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
} from '@mui/material';

interface DateTimePickerDialogProps {
  open: boolean;
  type: 'recruitmentStart' | 'deadline' | null;
  onClose: () => void;
  onConfirm: (datetime: string) => Promise<boolean>;
  isLoading: boolean;
}

/**
 * 日時選択ダイアログコンポーネント
 *
 * - yyyy-mm-dd hh:mm形式の日時入力
 * - 募集開始・締切両方で使用可能
 */
const DateTimePickerDialog: React.FC<DateTimePickerDialogProps> = memo(({
  open,
  type,
  onClose,
  onConfirm,
  isLoading,
}) => {
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');

  const resetState = useCallback(() => {
    setDate('');
    setTime('');
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const handleConfirm = useCallback(async () => {
    if (!date || !time) return;

    const datetime = `${date}T${time}:00`;
    const success = await onConfirm(datetime);

    if (success) {
      handleClose();
    }
  }, [date, time, onConfirm, handleClose]);

  const title = type === 'recruitmentStart' ? '募集開始日時設定' : '締切日時設定';
  const description = type === 'recruitmentStart'
    ? '募集を開始する日時を設定してください。'
    : '募集を締め切る日時を設定してください。';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {description}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="日付"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="時刻"
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Box>

        {date && time && (
          <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
            設定日時: {date} {time}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          キャンセル
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!date || !time || isLoading}
        >
          {isLoading ? '処理中...' : '設定'}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

DateTimePickerDialog.displayName = 'DateTimePickerDialog';

export default DateTimePickerDialog;
